import { classnames } from '@/helpers';
import { DayHeaderProps } from './DayHeader.types';

/**
 * Column header for a single day in the `_CVPickTimeWeekView` week grid.
 *
 * A weekday label over the day-of-month number over a sub-label (the month, or
 * a state word). `Today` shows the number on a dark pill; `Fully booked`/
 * `Closed` grey the whole cell and differ only by their sub-label copy;
 * `Campaign` reddens the sub-label. Today is a separate marker (mirrors
 * CalendarDay's `isToday`), so it can co-occur with campaign — a dark pill plus
 * a red sub-label.
 *
 * Purely presentational — the weekday/date/sub-label strings are formatted and
 * localized by the caller (the week view), matching CalendarDay.
 */
const DayHeader = ({
  weekday,
  dateNumber,
  subLabel,
  isSubLabelLoading = false,
  state = 'default',
  isToday = false,
  isNarrow = false,
  className,
}: DayHeaderProps) => {
  const isUnavailable = state === 'fullyBooked' || state === 'closed';
  const isCampaign = state === 'campaign';

  const weekdayColor = isUnavailable ? 'text-fg_disabled' : 'text-fg_secondary';
  const subLabelColor = isUnavailable ? 'text-fg_disabled' : isCampaign ? 'text-fg_highlight' : 'text-fg_secondary';
  const dateColor = isUnavailable ? 'text-fg_disabled' : 'text-fg_primary';

  // Opaque cell background so the header stays legible when the week view makes
  // this row sticky over the scrolling slots. Only the narrow variant greys
  // fully-booked/closed cells; desktop keeps the white card surface regardless
  // of availability.
  const cellBg = isNarrow && isUnavailable ? 'bg-bg_secondary_neutral' : 'bg-surface_l2';

  return (
    <div className={classnames('gap-2xs px-2xs py-xs flex flex-col items-center text-center', cellBg, className)}>
      <span className={classnames(isNarrow ? 'text-m' : 'text-l', weekdayColor)}>{weekday}</span>
      <span
        className={classnames(
          'flex items-center justify-center text-xl font-bold',
          isToday && !isUnavailable ? 'bg-bg_primary text-fg_on_primary px-xs rounded-full' : dateColor,
        )}>
        {dateNumber}
      </span>
      {isSubLabelLoading ? (
        // Sized to the sub-label line box (text-xs/text-s) so swapping in the
        // real label does not shift the header row.
        <div className={classnames('bg-surface_l4 animate-pulse rounded', isNarrow ? 'h-3 w-8' : 'h-3.5 w-10')} />
      ) : (
        subLabel && <span className={classnames(isNarrow ? 'text-xs' : 'text-s', subLabelColor)}>{subLabel}</span>
      )}
    </div>
  );
};

export default DayHeader;
